import { InventoryOwnerType } from "@1games-ru/region-shared/dist/enums/inventory/inventory";
import { InventoryItem } from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { union } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { OwnerType } from "@/domains/inventory/constants/ownerType";
import {
  equipmentSelector,
  inventoryByIdSelector,
  setMemoizedItem,
} from "@/domains/inventory/slice";
import getDisabledItemsByFilters from "@/domains/inventory/helpers/getDisabledItemsByFilters";
import useConfiscationItems from "@/domains/inventory/hooks/search/useConfiscationItems";
import useSearchManager from "@/domains/inventory/hooks/search/useSearchManager";
import useContextMenu from "@/domains/inventory/hooks/useContextMenu";
import useFilterItems from "@/domains/inventory/hooks/useFilterItems";
import useGetEquipment from "@/domains/inventory/hooks/useGetEquipment";
import useGetInventory from "@/domains/inventory/hooks/useGetInventory";
import useGetQuickSlots from "@/domains/inventory/hooks/useGetQuickSlots";
import usePlayers from "@/domains/inventory/hooks/usePlayers";
import useSearchItems from "@/domains/inventory/hooks/useSearchItems";
import InventorySearchSlotsContainer from "@/domains/inventory/containers/search/InventorySearchSlotsContainer";
import InventoryContent from "@/domains/inventory/components/InventoryContent";
import InventoryCoolDown from "@/domains/inventory/components/InventoryCoolDown";
import InventoryFilter from "@/domains/inventory/components/InventoryFilter";
import InventoryFooter from "@/domains/inventory/components/InventoryFooter";
import InventoryHeader from "@/domains/inventory/components/InventoryHeader";
import InventorySearch from "@/domains/inventory/components/InventorySearch";
import InventorySeparated from "@/domains/inventory/components/InventorySeparated";
import InventoryWeight from "@/domains/inventory/components/InventoryWeight";
import InventoryWrapper from "@/domains/inventory/components/InventoryWrapper";
import InventoryContextMenu from "@/domains/inventory/components/contextMenu/InventoryContextMenu";
import InventoryGrid from "@/domains/inventory/components/dndkit/InventoryGrid";
import InventorySearchHeader from "@/domains/inventory/components/search/InventorySearchHeader";
import InventorySearchIcon from "@/domains/inventory/components/search/InventorySearchIcon";

type Props = { inventoryId: number };

export default function InventorySearchContainer({ inventoryId }: Props) {
  const { handleContextMenu, isOpen, menuRef, position, itemForContextMenu, closeContextMenu } =
    useContextMenu();
  const dispatch = useAppDispatch();
  const inventory = useAppSelector((state) => inventoryByIdSelector(state, inventoryId));
  const { filteredItems, filterGroups, selectedGroups, onSelectHandler } = useFilterItems(
    inventory?.items || [],
  );
  const { onChangeSearchValue, deferredSearchValue, itemsBySearch } = useSearchItems(filteredItems);
  const { players } = usePlayers({ characterId: Number(inventory.ownerId) });
  const equipment = useAppSelector(equipmentSelector);
  const { disableItems: disabledItemsByConfiscate } = useConfiscationItems();
  const { getInventory } = useGetInventory();
  const { getEquipment } = useGetEquipment();
  const { getQuickSlots } = useGetQuickSlots();
  const { removeItemFromConfiscation, addItemToConfiscation } = useSearchManager();

  useEffect(() => {
    getEquipment({
      characterId: Number(inventory.ownerId),
    });
    getQuickSlots({
      characterId: Number(inventory.ownerId),
    });
  }, [inventory.ownerId]);

  const refreshInventory = useCallback(async () => {
    if (inventory) {
      getEquipment({
        characterId: Number(inventory.ownerId),
      });
      getQuickSlots({
        characterId: Number(inventory.ownerId),
      });
    }
  }, [inventory]);

  const momoizedItemHandler = useCallback(
    (item: InventoryItem | null) => dispatch(setMemoizedItem(item)),
    [],
  );

  const doubleClickItemHandler = useCallback((item: InventoryItem) => {
    switch (item.itemTemplate.type) {
      case OwnerType.Bag: {
        getInventory({
          inventoryOwnerId: item.id,
          inventoryOwnerType: InventoryOwnerType.Bag,
        });
        return;
      }
      default:
        return;
    }
  }, []);

  const disabledItemsByFilter = useMemo(() => {
    if (inventory) {
      return getDisabledItemsByFilters(inventory.items, itemsBySearch);
    }
    return [];
  }, [inventory, itemsBySearch]);

  const disabledItems = useMemo(
    () => union(disabledItemsByConfiscate, disabledItemsByFilter),
    [disabledItemsByConfiscate, disabledItemsByFilter],
  );

  const onCloseHandler = useCallback(() => {
    GameMpProvider.closeCurrentInterface();
  }, []);

  if (!players) return null;

  return (
    <InventoryWrapper className="max-h-full">
      <InventoryHeader className="px-0">
        <InventorySearchHeader
          name={players[inventory.ownerId].character.name.content || "-"}
          id={players[inventory.ownerId].character.id || 0}
          onClose={onCloseHandler}
        />
      </InventoryHeader>

      <InventoryContent className="flex flex-col gap-5.5 relative py-7.5">
        <InventorySearchIcon />
        <InventoryCoolDown
          percent={players[inventory.ownerId].player.level}
          size="md"
          className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 z-3"
        />
        <InventorySearchSlotsContainer
          characterId={inventory.ownerId}
          onRefresh={refreshInventory}
        />
      </InventoryContent>
      <InventoryContent className="flex items-center justify-between h-[3.75rem] border-t border-[#191919]">
        <div className="text-18 font-medium">Инвентарь</div>
        <div className="flex items-center gap-5">
          <InventorySearch value={deferredSearchValue} onChange={onChangeSearchValue} />
          {filterGroups.length > 0 && (
            <>
              <InventorySeparated />
              <InventoryFilter
                groups={filterGroups}
                selectedGroups={selectedGroups}
                onSelect={onSelectHandler}
              />
            </>
          )}
        </div>
      </InventoryContent>
      <InventoryContent className="flex-1 overflow-clip overflow-y-auto pb-6">
        <InventoryGrid
          inventoryId={inventory.id}
          equipmentId={equipment?.id}
          cols={inventory.size.width}
          rows={inventory.size.height}
          items={inventory.items}
          onRemoveItemFromConfiscation={removeItemFromConfiscation}
          onMemoizedItem={momoizedItemHandler}
          onDoubleClickItem={doubleClickItemHandler}
          disabledItems={disabledItems}
          onContextMenu={handleContextMenu}
        />
        {itemForContextMenu && isOpen && (
          <InventoryContextMenu
            ref={menuRef}
            position={position}
            item={itemForContextMenu}
            onAddItemToConfiscation={addItemToConfiscation}
            onClose={closeContextMenu}
          />
        )}
      </InventoryContent>
      <InventoryFooter>
        <InventoryWeight
          weight={inventory.weight + (equipment.weight || 0)}
          capacityWeight={inventory.maxWeight}
        />
      </InventoryFooter>
    </InventoryWrapper>
  );
}
